import axios from 'axios';
import { influencersSearchFiltersUrl } from '../../configurations/api/url';
import type { IInfluencer } from '../../redux/buzzbridge/types';

export type PlatformFilter = 'all' | 'instagram' | 'tiktok' | 'youtube' | 'facebook' | 'twitter';

export type InfluencerSearchFilters = {
    platform: PlatformFilter;
    gender: string;
    country: string;
    nationality: string;
    category: string;
    languages: string[];
    minFollowers: string;
    maxFollowers: string;
    minEngagementRate: string;
    maxEngagementRate: string;
};

export type InfluencerSearchOptions = {
    brandId?: string;
    campaignId?: string;
    /** Defaults to brand_match when brandId is set */
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
};

export const DEFAULT_INFLUENCER_SEARCH_FILTERS: InfluencerSearchFilters = {
    platform: 'all',
    gender: '',
    country: '',
    nationality: '',
    category: '',
    languages: [],
    minFollowers: '',
    maxFollowers: '',
    minEngagementRate: '',
    maxEngagementRate: '',
};

/** Display/API label for languages stored lowercase in `filterEnums`. */
export function formatLanguageForApi(lang: string): string {
    return lang
        .split(' ')
        .map(part => (part ? part.charAt(0).toUpperCase() + part.slice(1) : ''))
        .join(' ');
}

export function countActiveInfluencerFilters(filters: InfluencerSearchFilters): number {
    let n = filters.platform !== 'all' ? 1 : 0;
    if (filters.gender) n += 1;
    if (filters.country) n += 1;
    if (filters.nationality) n += 1;
    if (filters.category) n += 1;
    if (filters.languages.length > 0) n += 1;
    if (filters.minFollowers) n += 1;
    if (filters.maxFollowers) n += 1;
    if (filters.minEngagementRate) n += 1;
    if (filters.maxEngagementRate) n += 1;
    return n;
}

function appendEngagementParams(params: URLSearchParams, filters: InfluencerSearchFilters) {
    if (filters.minEngagementRate) params.set('min_engagement_rate', filters.minEngagementRate);
    if (filters.maxEngagementRate) params.set('max_engagement_rate', filters.maxEngagementRate);
}

function appendLanguageParams(params: URLSearchParams, languages: string[]) {
    if (languages.length === 0) return;
    if (languages.length === 1) {
        params.set('language', formatLanguageForApi(languages[0]));
        return;
    }
    params.set('languages', languages.map(formatLanguageForApi).join(','));
}

export function buildInfluencerSearchQueryParams(
    filters: InfluencerSearchFilters,
    search: string,
    page: number,
    limit: number,
    options?: InfluencerSearchOptions,
): URLSearchParams {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (filters.platform !== 'all') params.set('platform', filters.platform);
    if (filters.gender) params.set('gender', filters.gender);
    if (filters.country) params.set('country', filters.country);
    if (filters.nationality) params.set('nationality', filters.nationality);
    if (filters.category) params.set('category', filters.category);
    appendLanguageParams(params, filters.languages);
    if (filters.minFollowers) params.set('min_followers', filters.minFollowers);
    if (filters.maxFollowers) params.set('max_followers', filters.maxFollowers);
    appendEngagementParams(params, filters);
    if (options?.brandId) params.set('brand_id', options.brandId);
    if (options?.campaignId) params.set('campaign_id', options.campaignId);
    if (options?.sortBy) {
        params.set('sortBy', options.sortBy);
    } else if (options?.brandId) {
        params.set('sortBy', 'brand_match');
    }
    if (options?.sortOrder) {
        params.set('sortOrder', options.sortOrder);
    } else if (options?.brandId) {
        params.set('sortOrder', 'desc');
    }
    params.set('page', String(page));
    params.set('limit', String(limit));
    return params;
}

export type InfluencerSearchResult = {
    influencers: IInfluencer[];
    totalCount: number;
    totalPages: number;
};

function normalizeList(payload: any): IInfluencer[] {
    const list: IInfluencer[] =
        payload?.data?.influencers ?? payload?.data?.result ?? payload?.data ?? payload?.influencers ?? [];
    return Array.isArray(list) ? list : [];
}

export async function fetchInfluencerSearch(
    filters: InfluencerSearchFilters,
    search: string,
    page: number,
    limit: number,
    signal?: AbortSignal,
    token?: string,
    favoritesOnly?: boolean,
    options?: InfluencerSearchOptions,
): Promise<InfluencerSearchResult> {
    const params = buildInfluencerSearchQueryParams(filters, search, page, limit, options);
    if (favoritesOnly) params.set('favorites_only', 'true');
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    const res = await axios.get(`${influencersSearchFiltersUrl}?${params.toString()}`, { signal, headers });
    const payload = res.data;
    const list = normalizeList(payload);
    return {
        influencers: list,
        totalCount: payload?.totalCount ?? list.length,
        totalPages: payload?.totalPages ?? 1,
    };
}
